/**
 * TrameSessionQR.jsx — QR code « fin de session » au niveau d'une PHASE
 * complète (P1 / P2 / P3 ou équivalent).
 *
 * Différence avec TrameQR (compétence unique) :
 *  - QR pointe vers la trame filtrée sur la PHASE (pas une compétence)
 *    → l'élève retrouve TOUTES les compétences travaillées ce jour-là
 *  - Affichage plus grand + call-to-action visible en fin de séance
 *  - Prévu pour être PROJETÉ en fin de séance (mode enseignant) et
 *    scanné par les élèves avec leur téléphone.
 *
 * URL cible : /trame?phase=<phaseId>&mode=student
 */
import React, { useMemo, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { QrCode, Copy, Check, ScanLine, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

function buildSessionLink(phaseId) {
  if (typeof window === 'undefined') return '';
  const url = new URL(window.location.pathname, window.location.origin);
  url.searchParams.set('phase', phaseId);
  url.searchParams.set('mode', 'student');
  return url.toString();
}

export default function TrameSessionQR({
  phaseId,
  phaseCode,
  phaseTitle,
  color = '#e11d48',
}) {
  const link = useMemo(() => buildSessionLink(phaseId), [phaseId]);
  const [copied, setCopied] = useState(false);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success(`Lien de la session ${phaseCode || phaseId} copié.`);
      setTimeout(() => setCopied(false), 2200);
    } catch (_) {
      toast.error('Copie impossible. Long-press sur le QR pour partager.');
    }
  };

  if (!phaseId) return null;

  return (
    <section
      className="mt-8 rounded-2xl border overflow-hidden print:break-inside-avoid"
      style={{
        background: `linear-gradient(135deg, ${color}12 0%, transparent 70%)`,
        borderColor: `${color}55`,
      }}
      data-testid={`trame-session-qr-${phaseId}`}
    >
      <div className="p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-3">
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border"
            style={{ color, borderColor: `${color}55`, background: `${color}15` }}
          >
            <Sparkles className="w-3 h-3" /> Fin de session · {phaseCode || phaseId}
          </span>
        </div>

        <div className="flex flex-col md:flex-row items-start gap-5 md:gap-6">
          {/* QR géant */}
          <div
            className="bg-white p-2.5 rounded-xl flex-shrink-0 mx-auto md:mx-0"
            style={{ boxShadow: `0 0 0 2px ${color}66, 0 12px 30px ${color}22` }}
          >
            <QRCodeSVG
              value={link}
              size={168}
              level="M"
              bgColor="#ffffff"
              fgColor="#0a0a0b"
              data-testid={`session-qr-svg-${phaseId}`}
            />
          </div>

          {/* Contenu */}
          <div className="flex-1 min-w-0">
            <h3
              className="text-white font-black text-xl sm:text-2xl leading-tight print:text-black"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              Scanne ce QR à la fin de ta séance
            </h3>
            <p className="text-white/75 text-[13.5px] mt-2 leading-relaxed print:text-black/80">
              Tu retrouves <strong className="text-white print:text-black">toutes les compétences travaillées aujourd'hui</strong> ({phaseTitle}) directement sur ton téléphone — en <strong>mode élève</strong>, avec tes objectifs, comment tu sais que tu as réussi, et ce qu'il faut retenir.
            </p>

            <ul className="mt-3 space-y-1 text-white/70 text-[12.5px] print:text-black/70">
              <li className="flex items-start gap-1.5">
                <ScanLine className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color }} />
                <span>Ouvre l'appareil photo de ton téléphone</span>
              </li>
              <li className="flex items-start gap-1.5">
                <ScanLine className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color }} />
                <span>Vise le QR — le lien apparaît, tape-le</span>
              </li>
              <li className="flex items-start gap-1.5">
                <ScanLine className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color }} />
                <span>Ajoute la page à ton écran d'accueil pour la retrouver</span>
              </li>
            </ul>

            <div className="mt-4 flex flex-wrap items-center gap-2 print:hidden">
              <button
                type="button"
                onClick={copyLink}
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-white/15 bg-white/5 hover:bg-white/10 text-white/90 text-[11.5px] font-black uppercase tracking-widest transition"
                data-testid={`session-qr-copy-${phaseId}`}
              >
                {copied ? <Check className="w-3.5 h-3.5 text-emerald-300" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? 'Lien copié' : 'Copier le lien'}
              </button>
              <span className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-white/10 bg-black/40 text-white/50 text-[11px] font-mono">
                <QrCode className="w-3.5 h-3.5" style={{ color }} /> {phaseCode || phaseId}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
